import React, { useState } from "react";
import { Pie } from "react-chartjs-2";
import { Link } from "react-router-dom";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Helmet } from "react-helmet";

ChartJS.register(ArcElement, Tooltip, Legend);

const SipCalculator = ({ isSip, setIsSip }) => {
  const [monthlyInvestment, setMonthlyInvestment] = useState(25000);
  const [expectedReturnRate, setExpectedReturnRate] = useState(12);
  const [timePeriod, setTimePeriod] = useState(10);

  const calculateSIP = () => {
    const monthlyRate = expectedReturnRate / 100 / 12;
    const months = timePeriod * 12;
    const investedAmount = monthlyInvestment * months;
    const futureValue =
      monthlyInvestment *
      ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) *
      (1 + monthlyRate);
    const estimatedReturns = futureValue - investedAmount;

    return {
      investedAmount,
      estimatedReturns,
      totalValue: futureValue,
    };
  };

  const { investedAmount, estimatedReturns, totalValue } = calculateSIP();

  const data = {
    labels: ["Invested Amount", "Estimated Returns"],
    datasets: [
      {
        label: "SIP Returns Breakdown",
        data: [investedAmount, estimatedReturns],
        backgroundColor: ["#36A2EB", "#FF6384"],
      },
    ],
  };
  function Icon({ id, open }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
        >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
    );
  }
  
  
    const [activeIndex, setActiveIndex] = useState(0);
  
     
    const faqs = [
      {
        question: 'What is a Systematic Investment Plan (SIP)?',
        answer:
          'A Systematic Investment Plan (SIP) is a method of investing in mutual funds where you contribute a fixed amount at regular intervals—usually monthly. It promotes disciplined investing and helps build wealth gradually through the power of compounding.',
      },
      {
        question: 'Is SIP different from mutual funds?',
        answer:
          'Yes. SIP is a method of investing in mutual funds, not a mutual fund itself. You can invest in mutual funds either via a SIP (installments) or through a lump sum (one-time payment).',
      },
      {
        question: 'How does an SIP calculator work?',
        answer:
          'An SIP calculator uses a compound interest formula to estimate the future value of your monthly investments. It considers your monthly contribution, expected return rate, and investment duration to project a maturity amount.',
      },
      {
        question: 'Can an SIP calculator give exact returns?',
        answer:
          'No. SIP calculators provide estimated returns. Actual returns may differ due to market volatility, fund performance, expense ratios, and other external factors.',
      },
      {
        question: 'What information do I need to use an SIP calculator?',
        answer:
          'You need to enter: your monthly investment amount, investment duration (in years), and expected annual return rate. The calculator will then compute the maturity amount.',
      },
      {
        question: 'What is the formula used in an SIP calculator?',
        answer:
          'The formula is: M = P × [(1 + i)^n – 1] × (1 + i) / i, where M = Maturity amount, P = Monthly investment, i = Monthly interest rate (annual rate ÷ 12), and n = Number of months.',
      },
      {
        question: 'Can I use the SIP calculator for goal planning?',
        answer:
          'Absolutely. You can input different scenarios to see how much you need to invest monthly to reach a target goal in a specific time frame, making it an effective goal planning tool.',
      },
      {
        question: 'Is SIP suitable for short-term goals?',
        answer:
          'While SIPs are typically better for long-term goals (3+ years) due to market fluctuations, you can use them for short-term goals if you choose low-risk debt or liquid funds.',
      },
      {
        question: 'What return rate should I enter in the calculator?',
        answer:
          'You can enter an expected annual return rate based on the fund’s past performance. For equity funds, 10–12% is often assumed; for debt funds, it may range between 5–7%. However, these are not guaranteed.',
      },
      {
        question: 'Does the SIP calculator include taxes or fund expenses?',
        answer:
          'No. Most SIP calculators, including TheFinMaster’s, show gross estimates and do not factor in taxes (like capital gains tax), exit loads, or expense ratios. These should be considered separately.',
      },
      {
        question: 'What is the best duration for SIP investments?',
        answer:
          'The longer, the better. SIPs are most effective when held over a long-term horizon (5 years or more). This gives compounding time to work and helps smooth out market volatility through rupee cost averaging.',
      },
      {
        question: 'Can I change my SIP amount later?',
        answer:
          'Yes. Most mutual funds allow you to increase, decrease, pause, or stop your SIPs anytime. However, always check with the specific fund house for their terms and conditions.',
      },
      {
        question: 'What happens if I miss an SIP payment?',
        answer:
          'Missing one or two SIPs does not lead to penalties, but repeated defaults may result in the SIP being canceled. Your investment continues to remain in the fund, but you might lose out on the compounding benefits.',
      },
      {
        question: 'Is SIP better than a lump sum investment?',
        answer:
          'SIP is ideal for those who prefer to invest small amounts regularly and manage market volatility. Lump sum may be suitable when you have a large amount to invest and the market is reasonably valued. SIPs, however, are safer for beginners and more predictable in volatile markets.',
      },
      {
        question: 'How often should I use an SIP calculator?',
        answer:
          'You should use an SIP calculator whenever you set a new financial goal, plan a new SIP, want to review your current SIP performance, or consider increasing or changing your investment amount. Regular usage ensures your investment strategy stays aligned with your goals.',
      },
    ];
    
    const toggleAccordion = (index) => {
      if (activeIndex === index) {
          // If the clicked question is already expanded, collapse it
          setActiveIndex(null);
      } else {
          // If the clicked question is not expanded, expand it
          setActiveIndex(index);
      };
    }
  return (
    <>
      <Helmet>
        <title>
          SIP Calculator- Calculate Returns on SIP Investments | TheFinmaster
        </title>
        <meta
          name="description"
          content="Free SIP Calculator! Estimate your returns on Mutual Fund investments with Systematic Investment Plans."
        />
        <meta
          name="keywords"
          content="sip return rate, monthly sip calculator, step up sip, sip calculator online"
        />
        <link
          rel="canonical"
          href="https://thefinmaster.com/financial-tools-calculators/sip-calculator"
        />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>

      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800 ">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <Link
              to="/financial-tools-calculators"
              className="text-blue-600 hover:text-blue-800"
            >
              Financial Tools Calculators
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500 ">SIP Calculator</span>
          </li>
        </ol>
      </nav>
      <div className="py-6">
        <div className="container mx-auto p-4 shadow-xl rounded-md ">
          <div className="flex space-x-4 mb-4">
            <button
              className={`py-2 px-4 rounded-md ${
                isSip ? "bg-blue-500 text-white" : "bg-gray-200"
              }`}
              onClick={() => setIsSip(true)}
            >
              SIP
            </button>
            <button
              className={`py-2 px-4 rounded-md ${
                !isSip ? "bg-blue-500 text-white" : "bg-gray-200"
              }`}
              onClick={() => setIsSip(false)}
            >
              Lumpsum
            </button>
          </div>
          <h1 className="text-2xl font-bold mb-4">SIP Returns Calculator</h1>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Monthly Investment
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="100000"
                    value={monthlyInvestment}
                    onChange={(e) =>
                      setMonthlyInvestment(Number(e.target.value))
                    }
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">
                    ₹ {monthlyInvestment.toLocaleString()}
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100000"
                  value={monthlyInvestment}
                  onChange={(e) => setMonthlyInvestment(Number(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Expected Return Rate (P.A.)
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={expectedReturnRate}
                    onChange={(e) =>
                      setExpectedReturnRate(Number(e.target.value))
                    }
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">{expectedReturnRate} %</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="0.1"
                  value={expectedReturnRate}
                  onChange={(e) =>
                    setExpectedReturnRate(Number(e.target.value))
                  }
                  className="w-full mt-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Time Period (Years)
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={timePeriod}
                    onChange={(e) => setTimePeriod(Number(e.target.value))}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">{timePeriod} Yr</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="50"
                  value={timePeriod}
                  onChange={(e) => setTimePeriod(Number(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <button
                onClick={calculateSIP}
                className="bg-blue-500 text-white px-4 py-2 rounded"
              >
                Calculate
              </button>
            </div>
            <div>
              <div className="mb-4">
                <h2 className="text-xl font-bold">Results</h2>
                <p>Invested Amount: ₹ {investedAmount.toLocaleString()}</p>
                <p>Estimated Returns: ₹ {estimatedReturns.toLocaleString()}</p>
                <p>Total Value: ₹ {totalValue.toLocaleString()}</p>
              </div>
              <div>
                <h2 className="text-xl font-bold">Breakdown</h2>
                <div className="flex justify-center">
                  <Pie
                    data={data}
                    options={{ maintainAspectRatio: false }}
                    width={300}
                    height={300}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6">
        <div className="w-full md:w-2/3 p-2">
          {/* <h1 className="text-xl font-medium  bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
            Demystifying SIPs and Calculating Your Returns with Ease
          </h1>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg  mt-2">
            This rewritten content clarifies the concepts of SIPs (Systematic
            Investment Plans) and SIP calculators in a more reader-friendly way.
          </p> */}

          <div className="container mx-auto  py-2">
            <h1 className="text-xl font-medium  bg-blue-100 border-l-4 border-blue-600 pl-4 py-2 ">
              Demystifying SIPs and Calculating Your Returns with Ease
            </h1>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
              SIPs, or Systematic Investment Plans, have revolutionized how
              individuals invest in mutual funds. Whether you’re just stepping
              into the world of investing or looking to plan your long-term
              financial journey, SIPs offer a structured, convenient, and
              disciplined route. This page will help you understand the
              fundamentals of SIPs, decode how SIP calculators work, and explain
              how tools like TheFinMaster SIP Calculator can simplify your
              wealth-building process.
            </p>
          </div>
          <div className="container mx-auto  py-2">
            <h1 className="text-xl font-medium  bg-blue-100 border-l-4 border-blue-600 pl-4 py-2 mt-6">
              Understanding SIPs: A Stepping Stone to Mutual Fund Investing
            </h1>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
              A Systematic Investment Plan (SIP) is not a type of mutual
              fund—it’s a method of investing. Instead of putting a large lump
              sum into a mutual fund at once, SIP allows you to invest small,
              fixed amounts regularly, usually every month. This approach makes
              investing more accessible and less risky for the average investor.{" "}
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
              SIPs encourage financial discipline and help inculcate a savings
              habit. Since you invest consistently regardless of market
              conditions, you benefit from rupee cost averaging (buying more
              units when prices are low and fewer when prices are high) and
              power of compounding over time.{" "}
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
              Whether you’re investing for a dream vacation, a home, children’s
              education, or retirement, SIPs offer the flexibility to align with
              a wide variety of goals and timelines.{" "}
            </p>
          </div>
          <div className="container mx-auto  py-4">
            <h1 className="text-xl font-medium  bg-blue-100 border-l-4 border-blue-600 pl-4 py-2 ">
              What is an SIP Calculator? Your Investment Roadmap
            </h1>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
              An SIP calculator is a digital tool designed to project the future
              value of your SIP investments. You input three simple variables:{" "}
            </p>

            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6 mt-2">
              {" "}
              {/* Added pl-6 for left padding */}
              <span className="absolute left-0 top-0  text-blue-600">
                &#8226;
              </span>{" "}
              {/* Adjusted mt and added margin */}
              Your monthly investment amount
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6">
              {" "}
              {/* Added pl-6 for left padding */}
              <span className="absolute left-0 top-0  text-blue-600">
                &#8226;
              </span>{" "}
              The duration of your investment (in years)
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6">
              {" "}
              {/* Added pl-6 for left padding */}
              <span className="absolute left-0 top-0  text-blue-600">
                &#8226;
              </span>{" "}
              The expected annual return rate
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
              With these inputs, the calculator gives you an estimate of your
              maturity value, which is the total amount your SIP could grow into
              over time.
            </p>

            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
              It serves as a roadmap for financial planning—offering you
              clarity, control, and confidence in your investment decisions. By
              visualizing the outcome, you can adjust your contributions or
              timeline to better meet your goals.
            </p>
          </div>

          <div className="container mx-auto  py-4">
            <h1 className="text-xl font-medium  bg-blue-100 border-l-4 border-blue-600 pl-4 py-2 ">
              Important Note: Estimates and Transparency
            </h1>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
              While SIP calculators provide valuable insight, it's crucial to
              understand that these are estimates, not guarantees. Actual
              returns from mutual fund investments can vary due to:{" "}
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6 mt-2">
              {" "}
              {/* Added pl-6 for left padding */}
              <span className="absolute left-0 top-0  text-blue-600">
                &#8226;
              </span>{" "}
              {/* Adjusted mt and added margin */}
              Market fluctuations
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6">
              {" "}
              {/* Added pl-6 for left padding */}
              <span className="absolute left-0 top-0  text-blue-600">
                &#8226;
              </span>{" "}
              Fund performance
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6">
              {" "}
              {/* Added pl-6 for left padding */}
              <span className="absolute left-0 top-0  text-blue-600">
                &#8226;
              </span>{" "}
              Inflation
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6">
              {" "}
              {/* Added pl-6 for left padding */}
              <span className="absolute left-0 top-0  text-blue-600">
                &#8226;
              </span>{" "}
              Management fees and fund expenses
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
              Also, most calculators do not account for:
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6">
              {" "}
              {/* Added pl-6 for left padding */}
              <span className="absolute left-0 top-0  text-blue-600">
                &#8226;
              </span>{" "}
              Exit loads (fees for early withdrawal)
            </p>{" "}
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6">
              {" "}
              {/* Added pl-6 for left padding */}
              <span className="absolute left-0 top-0  text-blue-600">
                &#8226;
              </span>{" "}
              Expense ratios (annual fees charged by mutual funds)
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
              Therefore, while a calculator is an excellent starting point, you
              should also review the scheme details, risk factors, and consult a
              financial advisor if needed for personalized advice.
            </p>
          </div>
          <div className="container mx-auto  py-4">
            <h1 className="text-xl font-medium  bg-blue-100 border-l-4 border-blue-600 pl-4 py-2 ">
              Projecting Your Wealth Growth with SIPs
            </h1>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
              The power of SIP lies in its compounding effect—where your returns
              start earning additional returns. Over time, even small monthly
              contributions can accumulate into a sizable corpus.{" "}
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
              The SIP calculator helps you visualize this growth trajectory. You
              can see how much your investments can grow with different
              contribution levels or tenures. This visibility is crucial for:
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6 mt-2">
              {" "}
              {/* Added pl-6 for left padding */}
              <span className="absolute left-0 top-0  text-blue-600">
                &#8226;
              </span>{" "}
              {/* Adjusted mt and added margin */}
              Planning for long-term goals like education or retirement
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6">
              {" "}
              {/* Added pl-6 for left padding */}
              <span className="absolute left-0 top-0  text-blue-600">
                &#8226;
              </span>{" "}
              Estimating whether your current SIP strategy aligns with your
              target corpus
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6">
              {" "}
              {/* Added pl-6 for left padding */}
              <span className="absolute left-0 top-0  text-blue-600">
                &#8226;
              </span>{" "}
              Adjusting your current saving habits
            </p>

            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
              For example, increasing your SIP amount by just ₹500 a month can
              significantly boost your final amount, especially over a 10–15
              year horizon.
            </p>
          </div>

          <div className="container mx-auto  py-4">
            <h1 className="text-xl font-medium  bg-blue-100 border-l-4 border-blue-600 pl-4 py-2 ">
              Benefits of Using an SIP Calculator
            </h1>

            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6 mt-2">
              {" "}
              {/* Added pl-6 for left padding */}
              <span className="absolute left-0 top-0  text-blue-600">
                &#8226;
              </span>{" "}
              {/* Adjusted mt and added margin */}
              <span className="font-bold">
                {" "}
                Plan Your Investment Strategy :{" "}
              </span>{" "}
              Use the calculator to backtrack from your goal. Want ₹10 lakhs in
              10 years? The calculator tells you how much to invest monthly. It
              helps you convert your aspirations into actionable plans.
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6">
              {" "}
              {/* Added pl-6 for left padding */}
              <span className="absolute left-0 top-0  text-blue-600">
                &#8226;
              </span>{" "}
              {/* Adjusted mt and added margin */}
              <span className="font-bold">Track Your Progress : </span> Some
              calculators allow you to track how much you’ve invested so far and
              how close you are to your goal. This progress monitoring
              encourages you to stay on course and even top-up your SIP when
              feasible.
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6">
              {" "}
              {/* Added pl-6 for left padding */}
              <span className="absolute left-0 top-0  text-blue-600">
                &#8226;
              </span>{" "}
              {/* Adjusted mt and added margin */}
              <span className="font-bold">
                {" "}
                Estimate Returns Accurately :
              </span>{" "}
              Get an approximate idea of how much your investments will grow
              based on assumed returns. While not exact, it gives you a reliable
              ballpark figure for future planning.
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6">
              {" "}
              {/* Added pl-6 for left padding */}
              <span className="absolute left-0 top-0  text-blue-600">
                &#8226;
              </span>{" "}
              {/* Adjusted mt and added margin */}
              <span className="font-bold">Customize Scenarios :</span> Try out
              different scenarios—change your investment amount, duration, or
              return rate. This flexibility helps you find the most optimal path
              for your financial journey.
            </p>
          </div>

          <div className="container mx-auto  py-4">
            <h1 className="text-xl font-medium  bg-blue-100 border-l-4 border-blue-600 pl-4 py-2 ">
              How SIP Calculators Work: The Math Behind the Magic
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              SIP calculators use the following compound interest formula to
              estimate future value:
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6 mt-2">
              {" "}
              {/* Added pl-6 for left padding */}
              <span className="absolute left-0 top-0  text-blue-600">
                &#8226;
              </span>{" "}
              M = P × [(1 + i)^n – 1] × (1 + i) / i
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6 mt-2">
              {" "}
              {/* Added pl-6 for left padding */}
              <span className="absolute left-0 top-0  text-blue-600">
                &#8226;
              </span>{" "}
              {/* Adjusted mt and added margin */}M = Maturity amount
            </p>{" "}
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6 mt-2">
              {" "}
              {/* Added pl-6 for left padding */}
              <span className="absolute left-0 top-0  text-blue-600">
                &#8226;
              </span>{" "}
              {/* Adjusted mt and added margin */}P = Monthly SIP amount
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6 mt-2">
              {" "}
              {/* Added pl-6 for left padding */}
              <span className="absolute left-0 top-0  text-blue-600">
                &#8226;
              </span>{" "}
              {/* Adjusted mt and added margin */}i = Monthly interest rate
              (annual return ÷ 12 ÷ 100)
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6 mt-2">
              {" "}
              {/* Added pl-6 for left padding */}
              <span className="absolute left-0 top-0  text-blue-600">
                &#8226;
              </span>{" "}
              {/* Adjusted mt and added margin */}n = Total number of months
            </p>
          </div>

          <div className="container mx-auto  py-4">
            <h1 className="text-xl font-medium  bg-blue-100 border-l-4 border-blue-600 pl-4 py-2 ">
              {" "}
              <span className="font-bold">Example Calculation </span>Let’s
              assume:
            </h1>

            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6 mt-2">
              {" "}
              {/* Added pl-6 for left padding */}
              <span className="absolute left-0 top-0  text-blue-600">
                &#8226;
              </span>{" "}
              {/* Adjusted mt and added margin */}
              Monthly SIP = ₹1,000
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6 mt-2">
              {" "}
              {/* Added pl-6 for left padding */}
              <span className="absolute left-0 top-0  text-blue-600">
                &#8226;
              </span>{" "}
              {/* Adjusted mt and added margin */}Duration = 1 year (12 months)
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6 mt-2">
              {" "}
              {/* Added pl-6 for left padding */}
              <span className="absolute left-0 top-0  text-blue-600">
                &#8226;
              </span>{" "}
              {/* Adjusted mt and added margin */}
              Annual expected return = 12%
            </p>

            <h1 className="text-xl font-medium  bg-blue-100 border-l-4 border-blue-600 pl-4 py-2 ">
              {" "}
              Then:
            </h1>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6 mt-2">
              {" "}
              {/* Added pl-6 for left padding */}
              <span className="absolute left-0 top-0  text-blue-600">
                &#8226;
              </span>{" "}
              {/* Adjusted mt and added margin */}i = 12% ÷ 12 = 1% = 0.01
            </p>

            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6 mt-2">
              {" "}
              {/* Added pl-6 for left padding */}
              <span className="absolute left-0 top-0  text-blue-600">
                &#8226;
              </span>{" "}
              {/* Adjusted mt and added margin */}n = 12 months
            </p>
            <p className="text-xl font-medium   pl-4 py-2  text-gray-600">
              {" "}
              M = 1,000 × [(1 + 0.01)^12 – 1] × (1 + 0.01) ÷ 0.01 = ₹12,809
              (approx)
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              This showcases how your money grows every month, not just from
              your contributions but also from the returns on previously earned
              returns—a classic case of compound growth.
            </p>
          </div>

          <div className="container mx-auto  py-4">
            <h1 className="text-xl font-medium  bg-blue-100 border-l-4 border-blue-600 pl-4 py-2 ">
              Using TheFinMaster SIP Calculator: A Simple Process
            </h1>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
              TheFinMaster SIP calculator is designed to be intuitive and
              beginner-friendly. Here’s how to use it:{" "}
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6 mt-2">
              {" "}
              {/* Added pl-6 for left padding */}
              <span className="absolute left-0 top-0  text-blue-600">
                &#8226;
              </span>{" "}
              {/* Adjusted mt and added margin */}
              Enter your monthly SIP amount (e.g., ₹2,000)
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6">
              {" "}
              {/* Added pl-6 for left padding */}
              <span className="absolute left-0 top-0  text-blue-600">
                &#8226;
              </span>{" "}
              Choose your investment duration in years (e.g., 10 years)
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6">
              {" "}
              {/* Added pl-6 for left padding */}
              <span className="absolute left-0 top-0  text-blue-600">
                &#8226;
              </span>{" "}
              Input your expected annual return rate (e.g., 10%)
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
              The calculator instantly shows:
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6">
              {" "}
              {/* Added pl-6 for left padding */}
              <span className="absolute left-0 top-0  text-blue-600">
                &#8226;
              </span>{" "}
              Your total invested amount
            </p>{" "}
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6">
              {" "}
              {/* Added pl-6 for left padding */}
              <span className="absolute left-0 top-0  text-blue-600">
                &#8226;
              </span>{" "}
              The estimated returns earned
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6">
              {" "}
              {/* Added pl-6 for left padding */}
              <span className="absolute left-0 top-0  text-blue-600">
                &#8226;
              </span>{" "}
              The projected maturity amount
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
              You can use the tool multiple times to test different combinations
              and see how small changes in contributions or time frame can make
              a big difference.{" "}
            </p>
          </div>
          <div className="container mx-auto  py-2">
            <h1 className="text-xl font-medium  bg-blue-100 border-l-4 border-blue-600 pl-4 py-2 mt-6">
              TheFinMaster SIP Calculator: Your Investment Ally
            </h1>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
              Here’s why thousands of investors rely on TheFinMaster SIP
              Calculator:
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6">
              {" "}
              {/* Added pl-6 for left padding */}
              <span className="absolute left-0 top-0  text-blue-600">
                &#8226;
              </span>{" "}
              {/* Adjusted mt and added margin */}
              <span className="font-bold">
                {" "}
                Plan Your Investment with Confidence :
              </span>{" "}
              Get clear projections that help you decide how much to invest each
              month based on your life goals and time horizon.
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6">
              {" "}
              {/* Added pl-6 for left padding */}
              <span className="absolute left-0 top-0  text-blue-600">
                &#8226;
              </span>{" "}
              {/* Adjusted mt and added margin */}
              <span className="font-bold">
                {" "}
                Estimate Total Wealth Accumulation :
              </span>{" "}
              Visualize the total value you can accumulate by the end of your
              SIP tenure, helping you stay motivated and goal-focused.
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6">
              {" "}
              {/* Added pl-6 for left padding */}
              <span className="absolute left-0 top-0  text-blue-600">
                &#8226;
              </span>{" "}
              {/* Adjusted mt and added margin */}
              <span className="font-bold"> Eliminate Manual Errors :</span>{" "}
              Forget spreadsheets and manual math. Our tool does all the work
              instantly, saving you time and effort.
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6">
              {" "}
              {/* Added pl-6 for left padding */}
              <span className="absolute left-0 top-0  text-blue-600">
                &#8226;
              </span>{" "}
              {/* Adjusted mt and added margin */}
              <span className="font-bold">
                {" "}
                Build a Strong Savings Portfolio :{" "}
              </span>{" "}
              Use the calculator to optimize your SIP contributions across
              different funds, risk profiles, and durations. Make smarter,
              data-backed investment choices.
            </p>
          </div>

          <div className="container mx-auto  py-2">
            <h1 className="text-xl font-medium  bg-blue-100 border-l-4 border-blue-600 pl-4 py-2 mt-6">
              Empower Your Financial Journey with SIPs
            </h1>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
              SIPs are more than just a way to invest—they're a gateway to
              disciplined wealth creation. With tools like TheFinMaster SIP
              Calculator, you can plan wisely, invest consistently, and monitor
              your journey every step of the way.
            </p>
            <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
              Whether you're investing ₹500 or ₹5,000 a month, the key is
              consistency, clarity, and compounding. Start small, dream big, and
              let TheFinMaster guide you through every financial milestone.{" "}
            </p>
          </div>
        </div>

        <div className="w-full md:w-1/3 p-4">
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
              <h2 className="text-lg font-semibold ">Popular Categories</h2>
            </div>
            <div className="p-2 ">
              <ul>
                <Link to="/financial-tools-calculators/swp-calculator">
                  <li className="py-2  hover:bg-slate-100 border-b border-gray-300">
                    SWP Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/mutul-fund-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Mutual Fund Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/sukanya-samriddhi-yojana-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Samriddhi Yojana Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/fd-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    FD Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/rd-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    RD Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/nps-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    NPS Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/hra-exemption-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    HRA Exemption Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/retirement-planning-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Retirement Planning Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/emi-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    EMI Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/simple-interest-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Simple Interest Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/compound-interest-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Compound Interest Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/nsc-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    NSC Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/step-up-sip-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Step Up Sip Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/income-tax-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Income Tax Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/home-loan-emi-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Home Loan Emi Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/car-loan-emi-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Car Loan Emi Calculator
                  </li>
                </Link>
                {/* {categories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
              </ul>
            </div>
          </div>
          <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
            <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
              <h2 className="text-lg font-semibold">More Categories</h2>
            </div>
            <div className="p-2">
              <ul>
                <Link to="/financial-tools-calculators/gratuity-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Gratuity Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/APY-calculator">
                  <li className="py-2 border- hover:bg-slate-100 border-gray-300">
                    APY Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/cagr-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    CAGR Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/gst-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    GST Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/flat-vs-reducing-rate-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Flat Vs Reducing Rate Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/equity-margin-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Equity Margin Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/inflation-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Inflation Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/salary-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Salary Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/brokerage-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    Brokerage Calculator
                  </li>
                </Link>
                <Link to="/financial-tools-calculators/tds-calculator">
                  <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                    TDS Calculator
                  </li>
                </Link>
                {/* {moreCategories.map(category => (
          <Link key={category.id} to={`/category/${category.id}`}>
            <li className="py-2 border-b border-gray-300">{category.name}</li>
          </Link>
        ))} */}
              </ul>
            </div>
          </div>
        </div>
      </div>
             {/* FAQ section */}
    <div className="max-w-4xl mx-auto py-6 mt-10 px-2">
                <h1 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">Frequently Asked Questions</h1>
                <div className="space-y-4">
                    {faqs.map((faq, index) => (
                        <div key={index} className="border rounded-lg overflow-hidden shadow-md">
                            <button
                                onClick={() => toggleAccordion(index)}
                                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                            >
                                {faq.question}
                                <Icon id={index} open={activeIndex} />
                            </button>
                            {activeIndex === index && (
                                <div className="p-4 bg-gray-100">
                                    <p className="text-gray-700">{faq.answer}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
    </>
  );
};

export default SipCalculator;
